import {
  buildPayslip, dayLookup, occupationLabel, unworkedHolidays,
  type EmploymentTerms, type PayAdjustment, type Payslip, type Shift,
} from "@core";

import { supabase } from "./supabase";
import { zonedDate } from "./register";

/**
 * The payslip BCEA s33 requires, built on the desk rather than the phone.
 *
 * The numbers come from `buildPayslip` in core — the same function the app
 * calls, against the same rows. Two clients, one answer: an employer who
 * prints from a laptop and a worker who opens their copy on a phone must never
 * be able to hold two different documents for the same month.
 *
 * What differs here is only what the browser knows: the period is decided in
 * the **household's** timezone, the way the register does it, rather than in
 * whatever zone the laptop happens to be set to.
 */

function toTerms(t: Record<string, any>): EmploymentTerms {
  return {
    id: t.id,
    employeeId: t.employee_id,
    validFrom: t.valid_from,
    validTo: t.valid_to,
    rate: t.rate_cents / 100,
    currency: t.currency,
    ordinaryDaily: Number(t.ordinary_daily),
    ordinaryWeekly: Number(t.ordinary_weekly),
    breakMins: t.break_mins,
    breakAfter: Number(t.break_after_h),
    ot: Number(t.ot_multiplier),
    sunday: Number(t.sunday_multiplier),
    holiday: Number(t.holiday_multiplier ?? 2),
    daysPerWeek: t.days_per_week,
    annualLeaveDays: Number(t.annual_leave_days),
    sickLeaveDays: Number(t.sick_leave_days),
    sickCycleMonths: t.sick_cycle_months,
    familyLeaveDays: Number(t.family_leave_days),
  };
}

export function toAdjustment(r: Record<string, any>): PayAdjustment {
  return {
    id: r.id,
    employeeId: r.employee_id,
    onDate: r.on_date,
    direction: r.direction,
    amount: r.amount_cents / 100,
    purpose: r.purpose,
    basis: r.basis ?? undefined,
    note: r.note ?? undefined,
    createdAt: r.created_at,
  };
}

const ADJUSTMENT_COLS =
  "id, employee_id, on_date, direction, amount_cents, purpose, basis, note, created_at";

const pad = (n: number) => String(n).padStart(2, "0");

export function monthPeriod(year: number, month: number): { from: string; to: string } {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return { from: `${year}-${pad(month)}-01`, to: `${year}-${pad(month)}-${pad(last)}` };
}

export async function adjustmentsFor(
  employeeId: string, from: string, to: string,
): Promise<PayAdjustment[]> {
  const { data } = await supabase
    .from("pay_adjustments").select(ADJUSTMENT_COLS)
    .eq("employee_id", employeeId)
    .gte("on_date", from).lte("on_date", to)
    .order("on_date", { ascending: true });
  return (data ?? []).map(toAdjustment);
}

export interface PayslipResult {
  slip: Payslip;
  /** The rows behind the lines, for the s34(2)(d) quarter cap when adding one. */
  adjustments: PayAdjustment[];
}

export async function loadPayslip(
  employeeId: string,
  year: number,
  month: number,
  org: { name: string; timezone: string },
): Promise<PayslipResult | null> {
  const period = monthPeriod(year, month);

  const { data: employee } = await supabase
    .from("employees")
    .select("id, site_id, full_name, occupation, occupation_other")
    .eq("id", employeeId).maybeSingle();
  if (!employee) return null;

  // A day either side: the month's edges in the household's zone do not line up
  // with UTC. Rows are filtered by their zoned date below, so the padding only
  // ever offers candidates — it never admits a wrong one.
  const fromUtc = new Date(Date.UTC(year, month - 1, 0)).toISOString();
  const toUtc = new Date(Date.UTC(year, month, 2)).toISOString();

  const [siteRes, termRes, patternRes, shiftRes, leaveRes, adjustments] = await Promise.all([
    employee.site_id
      ? supabase.from("sites").select("address").eq("id", employee.site_id).maybeSingle()
      : Promise.resolve({ data: null }),
    supabase.from("employment_terms").select("*").eq("employee_id", employeeId),
    supabase.from("work_pattern").select("weekday").eq("employee_id", employeeId),
    supabase.from("shifts")
      .select("id, terms_id, clock_in, clock_out")
      .eq("employee_id", employeeId)
      .gte("clock_in", fromUtc).lt("clock_in", toUtc)
      .not("clock_out", "is", null)
      .order("clock_in", { ascending: true }),
    supabase.from("leave_requests").select("starts_on, ends_on")
      .eq("employee_id", employeeId).eq("status", "approved")
      .lte("starts_on", period.to).gte("ends_on", period.from),
    adjustmentsFor(employeeId, period.from, period.to),
  ]);

  const terms = new Map<string, EmploymentTerms>();
  for (const t of (termRes.data ?? []) as Record<string, any>[]) terms.set(t.id, toTerms(t));

  const all = [...terms.values()].sort((a, b) => b.validFrom.localeCompare(a.validFrom));
  // The rates printed on the page are the ones in force at the end of the
  // period. Each shift is still priced against its own term — see termsFor.
  const current =
    all.find((t) => t.validFrom <= period.to && (t.validTo === null || t.validTo >= period.to))
    ?? all[0];
  if (!current) return null;

  const shifts: Shift[] = [];
  const termOf = new Map<string, EmploymentTerms>();
  for (const row of (shiftRes.data ?? []) as Record<string, any>[]) {
    const clockIn = Date.parse(row.clock_in);
    if (!zonedDate(clockIn, org.timezone).startsWith(`${year}-${pad(month)}`)) continue;
    const shift = {
      id: row.id,
      clockIn,
      clockOut: row.clock_out ? Date.parse(row.clock_out) : null,
    } as Shift;
    shifts.push(shift);
    termOf.set(row.id, terms.get(row.terms_id) ?? current);
  }

  const weekdays = (patternRes.data ?? []) as { weekday: number }[];
  // No roster recorded is not "works nothing". Undefined means every rule that
  // reads it treats the day as one they would ordinarily work, which overpays a
  // public holiday rather than quietly taking it away.
  const worksWeekday = weekdays.length
    ? (weekday: number) => weekdays.some((w) => w.weekday === weekday)
    : undefined;

  const onLeave = new Set<string>();
  for (const r of (leaveRes.data ?? []) as { starts_on: string; ends_on: string }[]) {
    let d = new Date(`${r.starts_on}T00:00:00Z`);
    const end = new Date(`${r.ends_on}T00:00:00Z`);
    while (d <= end) {
      onLeave.add(d.toISOString().slice(0, 10));
      d = new Date(d.getTime() + 86_400_000);
    }
  }

  const worked = new Set(shifts.map((s) => zonedDate(s.clockIn, org.timezone)));
  const today = zonedDate(Date.now(), org.timezone);

  const unworked = unworkedHolidays({
    from: period.from, to: period.to, terms: current, worksWeekday,
    workedOn: (iso) => worked.has(iso),
    onLeave: (iso) => onLeave.has(iso),
    // A payslip states what is owed, and a public holiday that has not arrived
    // is not owed yet — nobody knows whether it will be worked.
    asAt: today,
  });

  const slip = buildPayslip({
    employer: { name: org.name, address: (siteRes.data as any)?.address ?? "" },
    employee: {
      name: employee.full_name,
      occupation: occupationLabel(employee.occupation, employee.occupation_other),
    },
    period,
    shifts,
    termsFor: (shift) => termOf.get(shift.id) ?? current,
    dayFor: dayLookup({ worksWeekday }),
    unworked,
    adjustments,
    terms: current,
    issuedOn: today,
  });

  return { slip, adjustments };
}

export type SaveResult =
  | { ok: true }
  | { ok: false; reason: "NOT_PERMITTED" | "INVALID" | "NETWORK" };

export async function saveAdjustment(employeeId: string, draft: {
  onDate: string;
  direction: "deduction" | "addition";
  amount: number;
  purpose: string;
  basis?: string;
}): Promise<SaveResult> {
  const { error } = await supabase.from("pay_adjustments").insert({
    employee_id: employeeId,
    on_date: draft.onDate,
    direction: draft.direction,
    amount_cents: Math.round(draft.amount * 100),
    purpose: draft.purpose.trim(),
    basis: draft.basis ?? null,
    // created_by is stamped by the trigger from the token, not sent from here.
  });
  if (!error) return { ok: true };
  if (error.code === "42501") return { ok: false, reason: "NOT_PERMITTED" };
  if (error.code === "23514") return { ok: false, reason: "INVALID" };
  return { ok: false, reason: "NETWORK" };
}
